import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Award, BadgeCheck, ShieldAlert } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../services/api';
import { LoadingScreen } from '../components/ui/LoadingScreen';
import { safeFormat } from '../utils/date';

type CertificateValidationResponse = {
  valid: boolean;
  data?: {
    participant: string;
    cargo: string;
    municipio: string;
    event: string;
    date: string;
    issued_at: string;
  };
};

export default function CertificateValidationPage() {
  const { code = '' } = useParams();
  const { data, isLoading, isError } = useQuery<CertificateValidationResponse>({
    queryKey: ['certificate-validation', code],
    queryFn: async () => (await api.get(`/certificates/validate/${encodeURIComponent(code)}`)).data,
    enabled: Boolean(code),
    retry: false,
  });

  if (isLoading) {
    return <LoadingScreen message="Validando certificado..." />;
  }

  const validationData = data?.data;
  const isValid = Boolean(data?.valid && validationData && !isError);

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
      <section className="mx-auto w-full max-w-2xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl">
        <header className="bg-brand-darkGreen px-6 py-8 text-center text-white sm:px-10">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
            <Award aria-hidden="true" size={30} />
          </div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-brand-yellow">ACONTURS Litoral</p>
          <h1 className="mt-2 text-2xl font-black">Validação de certificado</h1>
        </header>

        <div className="p-6 sm:p-10">
          {isValid ? (
            <div className="space-y-7">
              <div className="flex items-start gap-4 rounded-2xl border border-emerald-100 bg-emerald-50 p-5 text-emerald-950">
                <BadgeCheck className="mt-0.5 shrink-0 text-emerald-700" aria-hidden="true" size={26} />
                <div>
                  <h2 className="font-black">Certificado válido</h2>
                  <p className="mt-1 text-sm font-medium">Este documento foi emitido pela plataforma ACONTURS Litoral.</p>
                </div>
              </div>

              <dl className="divide-y divide-slate-100 rounded-2xl border border-slate-200 px-5">
                <ValidationDetail label="Participante" value={validationData.participant} />
                <ValidationDetail label="Evento" value={validationData.event} />
                <ValidationDetail label="Data do evento" value={safeFormat(validationData.date)} />
                <ValidationDetail label="Cargo" value={validationData.cargo || 'Não informado'} />
                <ValidationDetail label="Município" value={validationData.municipio || 'Não informado'} />
                <ValidationDetail label="Emitido em" value={safeFormat(validationData.issued_at, 'dd/MM/yyyy HH:mm')} />
              </dl>
            </div>
          ) : (
            <div className="space-y-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-700">
                <ShieldAlert aria-hidden="true" size={28} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900">Certificado não encontrado</h2>
                <p className="mt-2 text-sm font-medium leading-6 text-slate-500">Confira o código ou solicite ao participante o arquivo original emitido pela ACONTURS Litoral.</p>
              </div>
            </div>
          )}

          <Link to="/" className="mt-8 inline-flex min-h-11 w-full items-center justify-center rounded-2xl bg-slate-100 px-5 text-xs font-black uppercase tracking-widest text-slate-700 hover:bg-slate-200 transition-colors">
            Ir para o site da ACONTURS
          </Link>
        </div>
      </section>
    </main>
  );
}

function ValidationDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 py-4 sm:grid-cols-[10rem_1fr] sm:gap-4">
      <dt className="text-xs font-black uppercase tracking-widest text-slate-400">{label}</dt>
      <dd className="text-sm font-bold text-slate-800">{value}</dd>
    </div>
  );
}
